import React, { useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { Menu, MenuItem } from '@mui/material';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import { gql, useMutation as useApolloMutation, useQuery } from '@apollo/client';
import { addDays, format, isSameDay, isValid, parseISO } from 'date-fns';
import { queryCache } from 'react-query';
import ExpandSection from 'src/components/ExpandSection';
import {
  EventCompletionStatus,
  EventKind,
  EventsQuery,
  ObjectKind,
  useEventsQuery,
  useUpsertAssessmentMutation,
  useUpsertControlMutation,
  useUpsertEventMutation,
  useUpsertScenarioMutation,
} from 'src/graphql/_generated';
import { useAuth } from 'src/state/auth';
import useVersion from 'src/hooks/useVersion';
import { Poppins, StyledAvatar } from 'src/common';
import { useCache } from 'src/graphql';
import { QKeys, UserOptionType } from 'src/api/types';
import { showSuccessToast } from 'src/components/Toast';
import { Calendar } from 'src/assets/dynamicSvg/Calendar';
import { WarningSvg } from 'src/assets/dynamicSvg/WarningSvg';
import { PencilSvg } from 'src/assets/dynamicSvg/PencilSvg';
import { ChevronDown } from 'src/assets/dynamicSvg/ChevronDown';
import { getProfileCredentials } from 'src/utils/auth';
import { useStateSelector } from 'src/redux';
import MaterialDatePicker from 'src/components/form/MaterialDatePicker';
import UserInput from 'src/components/UserInput';

interface OwnerLike {
  id?: string | null;
  text?: string | null;
  email?: string | null;
  memberId?: string | null;
  inviteId?: string | null;
  profilePhotoPath?: string | null;
}

interface ReviewsSectionProps {
  objectType: ObjectKind;
  objectId: string;
  reviewDate?: string | null;
  owner?: OwnerLike | null;
}

type StatusValue = EventCompletionStatus.Open | EventCompletionStatus.Done | EventCompletionStatus.Cancelled;

type ObjectQueryKey = 'assessment' | 'scenario' | 'control' | 'incident';

type CadenceOption = 'None' | 'Monthly' | 'Quarterly' | 'Biannually' | 'Annually';

const objectQueryByType: Record<ObjectKind, ObjectQueryKey> = {
  [ObjectKind.Assessment]: 'assessment',
  [ObjectKind.Scenario]: 'scenario',
  [ObjectKind.Control]: 'control',
  [ObjectKind.Incident]: 'incident',
};

const statusOptions: { value: StatusValue; label: string }[] = [
  { value: EventCompletionStatus.Open, label: 'Not started' },
  { value: EventCompletionStatus.Done, label: 'Completed' },
  { value: EventCompletionStatus.Cancelled, label: 'Cancelled' },
];

const REVIEW_CADENCE_QUERY = gql`
  query reviewsSectionWorkspaceCadence($id: UxId) {
    workspace(id: $id) {
      id
      reviewCadence
      reviewCadenceDays
      actionsOnThisWorkspace {
        canEditSettings
      }
    }
  }
`;

const UPDATE_WORKSPACE_REVIEW_CADENCE = gql`
  mutation reviewsSectionUpdateWorkspaceCadence($workspaceId: UxId!, $reviewCadenceDays: Int) {
    upsertWorkspace(workspaceId: $workspaceId, reviewCadenceDays: $reviewCadenceDays) {
      id
      reviewCadence
      reviewCadenceDays
    }
  }
`;

const getStatusLabel = (value?: EventCompletionStatus | null) => {
  if (value === EventCompletionStatus.Done) return 'Completed';
  if (value === EventCompletionStatus.Cancelled) return 'Cancelled';
  return 'Not started';
};

const toDate = (value?: string | null) => {
  if (!value) return null;
  const parsed = parseISO(value);
  return isValid(parsed) ? parsed : null;
};

const formatLongDate = (value?: string | null, emptyValue = 'N/A') => {
  const parsed = toDate(value);
  return parsed ? format(parsed, 'dd MMMM yyyy') : emptyValue;
};

const formatNotes = (notes?: string | null) => {
  if (!notes) return [];
  return notes
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
};

const isOpenLike = (value?: EventCompletionStatus | null) =>
  value === null || value === undefined || value === EventCompletionStatus.Open;

type ReviewEvent = EventsQuery['events'][number];

const sortDateValue = (event: ReviewEvent) => {
  const date = event.completedAt || event.dueDate || event.meta?.createdAt;
  const parsed = toDate(date);
  return parsed ? parsed.getTime() : 0;
};

const eventMatchesDate = (eventDate?: string | null, reviewDate?: string | null) => {
  const eventParsed = toDate(eventDate);
  const reviewParsed = toDate(reviewDate);
  if (!eventParsed || !reviewParsed) return false;
  return isSameDay(eventParsed, reviewParsed);
};

const buildOwnerInput = (owner?: OwnerLike | UserOptionType | null) => {
  if (!owner) return undefined;

  const payload = {
    ...(owner.id ? { id: owner.id } : {}),
    ...(owner.text ? { text: owner.text } : {}),
    ...(owner.email ? { email: owner.email } : {}),
    ...(owner.memberId ? { memberId: owner.memberId } : {}),
    ...(owner.inviteId ? { inviteId: owner.inviteId } : {}),
    ...(owner.profilePhotoPath ? { profilePhotoPath: owner.profilePhotoPath } : {}),
  };

  return Object.keys(payload).length ? payload : undefined;
};

const ownerUniqueKey = (owner?: OwnerLike | null) => {
  if (!owner) return '';
  return owner.id || owner.memberId || owner.inviteId || owner.email || owner.text || '';
};

const asUserOption = (owner?: OwnerLike | null): UserOptionType => ({
  id: owner?.id || undefined,
  text: owner?.text || '',
  email: owner?.email || undefined,
  memberId: owner?.memberId || undefined,
  inviteId: owner?.inviteId || undefined,
  profilePhotoPath: owner?.profilePhotoPath || undefined,
});

const toCadenceLabel = (days?: number | null) => {
  if (days === null || days === undefined || days <= 0) return 'None';
  if (days === 30) return 'Monthly';
  if (days === 90) return 'Quarterly';
  if (days === 182) return 'Biannually';
  if (days === 365) return 'Annually';
  return `${days} days`;
};

const cadenceOptions: { label: CadenceOption; days: number }[] = [
  { label: 'None', days: 0 },
  { label: 'Monthly', days: 30 },
  { label: 'Quarterly', days: 90 },
  { label: 'Biannually', days: 182 },
  { label: 'Annually', days: 365 },
];

const cadenceLabelToDays: Record<CadenceOption, number> = cadenceOptions.reduce((acc, option) => {
  acc[option.label] = option.days;
  return acc;
}, {} as Record<CadenceOption, number>);

const normalizeCadenceLabel = (value?: string | null): CadenceOption | null => {
  if (!value) return null;

  if (value === 'None' || value === 'Monthly' || value === 'Quarterly' || value === 'Biannually' || value === 'Annually') {
    return value;
  }

  return null;
};

const cadenceLabelFromDays = (days?: number | null): CadenceOption | null => {
  const match = cadenceOptions.find((option) => option.days === (days ?? -1));
  return match?.label || null;
};

const nextDateFromCadenceDays = (sourceDate: string, cadenceDays?: number | null): string | null => {
  const parsed = toDate(sourceDate);
  if (!parsed) return null;

  const days = cadenceDays ?? 0;
  if (days <= 0) return null;

  return addDays(parsed, days).toISOString();
};

const Div = styled.div`
  .panel {
    border: 1px solid ${({ theme }) => theme.colors.stroke};
    border-radius: 6px;
    padding: 18px;
  }

  .grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    grid-gap: 30px;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    .panel {
      padding: 14px;
    }

    .grid {
      grid-template-columns: 1fr;
      grid-gap: 24px;
    }
  }
`;

const ColumnTitle = styled(Poppins).attrs({ px: 16, weight: 600 })`
  display: block;
  margin-bottom: 16px;
`;

const RowCard = styled.div<{ $danger?: boolean }>`
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.white};
  padding: 14px;

  ${({ $danger }) =>
    $danger &&
    css`
      background: rgba(230, 46, 120, 0.08);
    `}
`;

const CadenceRow = styled(RowCard)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
`;

const EditIconButton = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const AvatarPill = styled.div`
  width: fit-content;
  margin: -2px 0 10px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0 4px 16px ${({ theme }) => theme.colors.shadowLight};
  padding: 6px 10px;

  .avatars {
    display: flex;
    align-items: center;
  }

  .avatars .avatar:not(:first-child) {
    margin-left: -8px;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    margin: 0 0 12px;
  }
`;

const Flex = styled.div`
  display: flex;
  align-items: center;
`;

const LabelText = styled(Poppins).attrs({ px: 14, color: 'cflowerBlue' })``;

const ValueText = styled(Poppins).attrs({ px: 14, weight: 600 })`
  line-height: 1.2;
`;

const IconFrame = styled.div<{ $small?: boolean; $warning?: boolean }>`
  width: ${({ $small }) => ($small ? 28 : 38)}px;
  height: ${({ $small }) => ($small ? 28 : 38)}px;
  border: 1px solid ${({ theme }) => theme.colors.gp1};
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme, $warning }) => ($warning ? 'rgba(230, 46, 120, 0.06)' : theme.colors.white)};
  flex-shrink: 0;
`;

const OverdueTag = styled.span`
  margin-left: 8px;
  height: 22px;
  padding: 0 10px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.error};
  color: ${({ theme }) => theme.colors.white};
  font-size: 12px;
  font-weight: 500;
`;

const Divider = styled.div`
  height: 1px;
  width: 100%;
  background: ${({ theme }) => theme.colors.stroke};
  margin: 12px 0;
`;

const UpcomingMetaRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-gap: 10px;
  align-items: center;
`;

const ValueWithEdit = styled(Flex)`
  gap: 10px;
  justify-content: flex-end;
`;

const InlineEditorRow = styled.div`
  margin-top: 12px;
`;

const InlineActions = styled.div`
  margin-top: 10px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const TextButton = styled.button<{ $primary?: boolean }>`
  height: 30px;
  min-width: 72px;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  background: ${({ $primary, theme }) => ($primary ? theme.colors.gradient : theme.colors.white)};
  color: ${({ $primary, theme }) => ($primary ? theme.colors.white : theme.colors.prussianBlue)};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
`;

const OwnerInputWrapper = styled.div`
  .MuiFormControl-root {
    width: 100%;
  }
`;

const DateInputWrapper = styled.div`
  .MuiFormControl-root {
    width: 100%;
  }
`;

const OwnerIcon = () => {
  const colors = useStateSelector(({ theme }) => theme.colors);

  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M10.0004 10.0001C11.8413 10.0001 13.3337 8.50776 13.3337 6.66683C13.3337 4.82588 11.8413 3.3335 10.0004 3.3335C8.15944 3.3335 6.66699 4.82588 6.66699 6.66683C6.66699 8.50776 8.15944 10.0001 10.0004 10.0001Z"
        stroke={colors.gp1}
        strokeWidth="1.5"
      />
      <path
        d="M3.33301 16.667C3.33301 14.0897 6.31801 12.0003 9.99967 12.0003C13.6813 12.0003 16.6663 14.0897 16.6663 16.667"
        stroke={colors.gp1}
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
};

const NotesCard = styled.div`
  margin-top: 12px;
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  border-radius: 6px;
  padding: 0 14px;

  .head {
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
  }

  .body {
    border-top: 1px solid ${({ theme }) => theme.colors.stroke};
    padding: 12px 0;
  }
`;

const NotesText = styled(Poppins).attrs({ px: 14 })`
  display: block;
  margin-bottom: 6px;
`;

const NotesList = styled.ul`
  margin: 0;
  padding-left: 18px;

  li {
    margin: 4px 0;
    color: ${({ theme }) => theme.colors.prussianBlue};
    font-size: 14px;
    font-weight: 500;
  }
`;

const NotesHead = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const NotesActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const TinyTextButton = styled.button`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.gp1};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
`;

const NotesTextArea = styled.textarea`
  width: 100%;
  min-height: 94px;
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  border-radius: 6px;
  padding: 10px;
  resize: vertical;
  font-size: 14px;
  font-family: inherit;
  color: ${({ theme }) => theme.colors.prussianBlue};
`;

const TimelineColumn = styled.div`
  position: relative;
  padding-left: 2px;
`;

const PreviousReviewsScroller = styled.div`
  max-height: 640px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 6px;
`;

const TimelineItem = styled.div`
  position: relative;
  padding-left: 52px;

  &:not(:last-child) {
    margin-bottom: 22px;
  }

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    left: 12px;
    top: 16px;
    bottom: -26px;
    width: 5px;
    border-radius: 6px;
    background: ${({ theme }) => theme.colors.gradientVertical};
    z-index: 0;
  }

  .marker {
    position: absolute;
    top: 2px;
    left: 0;
    z-index: 1;
  }
`;

const TimelineHeader = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-gap: 10px;
  align-items: center;
`;

const ScheduledRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-gap: 10px;
  align-items: end;
`;

const RightAlign = styled.div`
  text-align: right;
`;

const EmptyState = styled(Poppins).attrs({ px: 14, color: 'cflowerBlue' })`
  display: block;
  padding: 12px 0;
`;

const StatusButton = styled.button<{ $status?: EventCompletionStatus | null }>`
  border: 1px solid
    ${({ theme, $status }) => ($status === EventCompletionStatus.Done ? 'transparent' : theme.colors.stroke)};
  background: ${({ $status }) => {
    if ($status === EventCompletionStatus.Done) return '#C8F0E3';
    if ($status === EventCompletionStatus.Cancelled) return 'rgba(225, 61, 61, 0.12)';
    return 'transparent';
  }};
  color: ${({ theme, $status }) =>
    $status === EventCompletionStatus.Cancelled ? theme.colors.statusRed : theme.colors.prussianBlue};
  border-radius: 999px;
  min-width: 120px;
  height: 36px;
  padding: 0 9px;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  grid-gap: 8px;
  cursor: pointer;

  ${({ disabled }) =>
    disabled &&
    css`
      cursor: default;
      opacity: 0.85;
    `}
`;

const StatusLeft = styled.span`
  display: inline-flex;
  align-items: center;
  grid-gap: 6px;
  min-width: 0;
`;

const StatusDot = styled.span<{ $status?: EventCompletionStatus | null }>`
  width: 11px;
  height: 11px;
  border-radius: 50%;
  display: inline-block;
  border: 1px solid
    ${({ theme, $status }) => {
      if ($status === EventCompletionStatus.Done) return theme.colors.statusGreen;
      if ($status === EventCompletionStatus.Cancelled) return theme.colors.statusRed;
      return theme.colors.cflowerBlue;
    }};
  position: relative;

  &::after {
    content: '';
    position: absolute;
    left: 2px;
    top: 2px;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: ${({ theme, $status }) => {
      if ($status === EventCompletionStatus.Done) return theme.colors.statusGreen;
      if ($status === EventCompletionStatus.Cancelled) return theme.colors.statusRed;
      return theme.colors.cflowerBlue;
    }};
  }
`;

const StatusLabel = styled(Poppins).attrs({ px: 12 })`
  white-space: nowrap;
`;

interface StatusPillProps {
  value?: EventCompletionStatus | null;
  disabled?: boolean;
  onSelect: (status: StatusValue) => void;
}

const StatusPill: React.FC<StatusPillProps> = ({ value, disabled, onSelect }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <>
      <StatusButton
        type="button"
        $status={value}
        disabled={disabled}
        onClick={(e) => {
          if (disabled) return;
          setAnchorEl(e.currentTarget);
        }}
      >
        <StatusLeft>
          <StatusDot $status={value} />
          <StatusLabel>{getStatusLabel(value)}</StatusLabel>
        </StatusLeft>
        <ChevronDown color="#BFC7DC" />
      </StatusButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {statusOptions.map((option) => (
          <MenuItem
            key={option.value}
            selected={option.value === (value ?? EventCompletionStatus.Open)}
            onClick={() => {
              onSelect(option.value);
              setAnchorEl(null);
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

interface ReviewNotesProps {
  notes?: string | null;
  placeholderItems: string[];
  defaultOpen?: boolean;
  editable?: boolean;
  canEdit?: boolean;
  saving?: boolean;
  onSave?: (value: string) => Promise<void>;
}

const ReviewNotes: React.FC<ReviewNotesProps> = ({
  notes,
  placeholderItems,
  defaultOpen = false,
  editable = false,
  canEdit = false,
  saving = false,
  onSave,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(notes || '');

  const lines = useMemo(() => formatNotes(notes), [notes]);

  useEffect(() => {
    if (!isEditing) {
      setDraft(notes || '');
    }
  }, [notes, isEditing]);

  return (
    <NotesCard>
      <div className="head" onClick={() => setOpen((v) => !v)}>
        <LabelText>Notes</LabelText>
        <NotesHead>
          {editable && canEdit && (
            <NotesActions onClick={(e) => e.stopPropagation()}>
              {!isEditing ? (
                <EditIconButton
                  type="button"
                  onClick={() => {
                    setOpen(true);
                    setIsEditing(true);
                  }}
                >
                  <PencilSvg />
                </EditIconButton>
              ) : (
                <>
                  <TinyTextButton
                    type="button"
                    onClick={async () => {
                      if (!onSave) {
                        setIsEditing(false);
                        return;
                      }
                      await onSave(draft.trim());
                      setIsEditing(false);
                    }}
                    disabled={saving}
                  >
                    Save
                  </TinyTextButton>
                  <TinyTextButton
                    type="button"
                    onClick={() => {
                      setDraft(notes || '');
                      setIsEditing(false);
                    }}
                    disabled={saving}
                  >
                    Cancel
                  </TinyTextButton>
                </>
              )}
            </NotesActions>
          )}
          <span style={{ transform: open ? 'rotate(0deg)' : 'rotate(-90deg)', display: 'inline-flex' }}>
            <ChevronDown color="#C9D0E6" />
          </span>
        </NotesHead>
      </div>
      {open && (
        <div className="body">
          {isEditing ? (
            <NotesTextArea value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Add notes" />
          ) : lines.length ? (
            lines.map((line, idx) => <NotesText key={`${line}-${idx}`}>{line}</NotesText>)
          ) : (
            <>
              <NotesText>Notes about review can be added here like:</NotesText>
              <NotesList>
                {placeholderItems.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </NotesList>
            </>
          )}
        </div>
      )}
    </NotesCard>
  );
};

const ReviewsSection: React.FC<ReviewsSectionProps> = ({ objectType, objectId, reviewDate, owner }) => {
  const { workspaceId, isPlatformAdmin, features } = useAuth();
  const { versionId } = useVersion();
  const { refetchQuery } = useCache();
  const reviewsFeatureEnabled = isPlatformAdmin && !!features?.['review.iteration3'];

  const [isOpen, setIsOpen] = useState(true);
  const [localReviewDate, setLocalReviewDate] = useState<string | null>(reviewDate || null);
  const [localOwner, setLocalOwner] = useState<OwnerLike | null>(owner || null);

  const [isEditingDate, setIsEditingDate] = useState(false);
  const [draftReviewDate, setDraftReviewDate] = useState<string | null>(reviewDate || null);

  const [isEditingOwner, setIsEditingOwner] = useState(false);
  const [draftOwner, setDraftOwner] = useState<UserOptionType>(asUserOption(owner));
  const [cadenceAnchorEl, setCadenceAnchorEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setLocalReviewDate(reviewDate || null);
    setDraftReviewDate(reviewDate || null);
  }, [reviewDate]);

  useEffect(() => {
    setLocalOwner(owner || null);
    setDraftOwner(asUserOption(owner));
  }, [owner?.id, owner?.text, owner?.email, owner?.inviteId, owner?.memberId, owner?.profilePhotoPath]);

  const canEdit = reviewsFeatureEnabled && !versionId;

  const {
    data,
    loading,
    refetch: refetchEvents,
    error,
  } = useEventsQuery({
    variables: {
      workspaceId: workspaceId!,
      objectType,
      objectId,
      eventType: EventKind.Review,
    },
    skip: !workspaceId || !isOpen || !reviewsFeatureEnabled,
    fetchPolicy: 'cache-and-network',
  });

  const { data: workspaceData } = useQuery(REVIEW_CADENCE_QUERY, {
    variables: { id: workspaceId },
    skip: !workspaceId || !isOpen || !reviewsFeatureEnabled,
    fetchPolicy: 'cache-and-network',
  });

  const [upsertEvent, { loading: savingEvent }] = useUpsertEventMutation();
  const [upsertScenario, { loading: savingScenario }] = useUpsertScenarioMutation();
  const [upsertControl, { loading: savingControl }] = useUpsertControlMutation();
  const [upsertAssessment, { loading: savingAssessment }] = useUpsertAssessmentMutation();
  const [updateWorkspaceCadence, { loading: savingCadence }] = useApolloMutation(UPDATE_WORKSPACE_REVIEW_CADENCE);

  const reviewEvents = useMemo(() => {
    const list = data?.events || [];
    return [...list].sort((a, b) => sortDateValue(b) - sortDateValue(a));
  }, [data?.events]);

  const upcomingReviewEvent = useMemo(() => {
    const byDate = reviewEvents.find((event) => eventMatchesDate(event.dueDate, localReviewDate));
    if (byDate) return byDate;

    const latestOpen = reviewEvents.find((event) => isOpenLike(event.completionStatus));
    return latestOpen;
  }, [reviewEvents, localReviewDate]);

  const effectiveOwner: OwnerLike | null = localOwner || (upcomingReviewEvent?.assignedTo as OwnerLike | null) || null;

  const previousReviews = useMemo(
    () => reviewEvents.filter((event) => event.id !== upcomingReviewEvent?.id),
    [reviewEvents, upcomingReviewEvent?.id],
  );

  const workspaceCadenceDays = (workspaceData?.workspace?.reviewCadenceDays as number | null) ?? null;
  const workspaceCadence = normalizeCadenceLabel(workspaceData?.workspace?.reviewCadence as string | null);
  const cadenceLabel = workspaceCadence || cadenceLabelFromDays(workspaceCadenceDays) || null;
  const showEnumCadence = objectType === ObjectKind.Scenario || objectType === ObjectKind.Control;
  const cadenceDisplayLabel = showEnumCadence ? cadenceLabel || toCadenceLabel(workspaceCadenceDays) : toCadenceLabel(workspaceCadenceDays);
  const canEditCadence =
    canEdit && showEnumCadence && !!workspaceData?.workspace?.actionsOnThisWorkspace?.canEditSettings;

  const upcomingStatus = (upcomingReviewEvent?.completionStatus ?? EventCompletionStatus.Open) as StatusValue;

  const reviewDateInPast = (() => {
    const parsed = toDate(localReviewDate);
    return parsed ? parsed.getTime() < Date.now() : false;
  })();

  const isUpcomingOverdue = reviewDateInPast && isOpenLike(upcomingStatus);

  const ownerName = effectiveOwner?.text || effectiveOwner?.email || 'N/A';

  const avatarOwners = useMemo(() => {
    const candidates: OwnerLike[] = [
      effectiveOwner || undefined,
      ...reviewEvents.map((event) => event.completedBy || undefined),
      ...reviewEvents.map((event) => event.assignedTo || undefined),
    ].filter(Boolean) as OwnerLike[];

    const unique = candidates.filter((candidate, idx, arr) => {
      const key = ownerUniqueKey(candidate);
      if (!key) return false;
      return arr.findIndex((item) => ownerUniqueKey(item) === key) === idx;
    });

    return unique.slice(0, 2);
  }, [effectiveOwner, reviewEvents]);

  const saving = savingEvent || savingScenario || savingControl || savingAssessment || savingCadence;

  const refetchEverything = async () => {
    await Promise.all([
      refetchEvents(),
      refetchQuery({ include: ['events', objectQueryByType[objectType], 'workspace'] }),
      queryCache.invalidateQueries([QKeys.Audit]),
    ]);
  };

  const upsertObject = async ({ nextDate, nextOwner }: { nextDate?: string | null; nextOwner?: OwnerLike | null }) => {
    if (objectType === ObjectKind.Scenario) {
      await upsertScenario({
        variables: {
          scenarioData: {
            id: objectId,
            ...(nextDate !== undefined ? { reviewDate: nextDate } : {}),
            ...(nextOwner !== undefined ? { riskOwner: buildOwnerInput(nextOwner) } : {}),
          },
        },
      });
      return;
    }

    if (objectType === ObjectKind.Control) {
      if (!workspaceId) return;
      await upsertControl({
        variables: {
          workspaceId,
          controlData: {
            id: objectId,
            ...(nextDate !== undefined ? { reviewDate: nextDate } : {}),
            ...(nextOwner !== undefined ? { owner: buildOwnerInput(nextOwner) } : {}),
          },
        },
      });
      return;
    }

    if (objectType === ObjectKind.Assessment) {
      await upsertAssessment({
        variables: {
          assessmentData: {
            id: objectId,
            ...(nextDate !== undefined ? { reviewDate: nextDate } : {}),
          },
        },
      });
    }
  };

  const syncUpcomingReviewEvent = async ({
    dueDate,
    ownerValue,
    notes,
    forceCreate,
  }: {
    dueDate?: string | null;
    ownerValue?: OwnerLike | null;
    notes?: string | null;
    forceCreate?: boolean;
  }) => {
    if (!workspaceId || !dueDate) return;

    const targetOwner = ownerValue === undefined ? effectiveOwner : ownerValue;
    const ownerInput = buildOwnerInput(targetOwner);

    const canReuseUpcoming =
      !!upcomingReviewEvent?.id && !forceCreate && upcomingReviewEvent.completionStatus !== EventCompletionStatus.Done;

    const payload = {
      ...(canReuseUpcoming ? { id: upcomingReviewEvent?.id } : {}),
      eventType: EventKind.Review,
      objectType,
      objectId,
      dueDate,
      completionStatus: EventCompletionStatus.Open,
      ...(ownerInput ? { assignedTo: ownerInput } : {}),
      ...(notes !== undefined ? { notes } : {}),
    };

    await upsertEvent({
      variables: {
        workspaceId,
        eventData: [payload],
      },
    });
  };

  const updateStatus = async ({ status, eventId, dueDate, completedAt }: { status: StatusValue; eventId?: string; dueDate?: string | null; completedAt?: string | null }) => {
    if (!workspaceId) return;

    const ownerInput = buildOwnerInput(effectiveOwner);

    const completedAtValue = status === EventCompletionStatus.Done ? completedAt || new Date().toISOString() : null;

    const payload = {
      ...(eventId ? { id: eventId } : {}),
      eventType: EventKind.Review,
      objectType,
      objectId,
      completionStatus: status,
      completedAt: completedAtValue,
      ...(dueDate ? { dueDate } : {}),
      ...(!eventId && ownerInput ? { assignedTo: ownerInput } : {}),
    };

    await upsertEvent({
      variables: {
        workspaceId,
        eventData: [payload],
      },
    });

    if (
      status === EventCompletionStatus.Done &&
      eventId &&
      completedAtValue &&
      (workspaceCadenceDays ?? 0) > 0 &&
      eventId === upcomingReviewEvent?.id
    ) {
      const nextDate = nextDateFromCadenceDays(completedAtValue, workspaceCadenceDays);
      if (nextDate) {
        await upsertObject({ nextDate });
        setLocalReviewDate(nextDate);

        await syncUpcomingReviewEvent({
          dueDate: nextDate,
          ownerValue: effectiveOwner,
          forceCreate: true,
        });
      }
    }

    await refetchEverything();
    showSuccessToast({ title: 'Review updated successfully!' });
  };

  const saveCadence = async (nextCadence: CadenceOption) => {
    if (!workspaceId || !canEditCadence) return;

    const nextDays = cadenceLabelToDays[nextCadence];
    if (nextDays === workspaceCadenceDays) {
      setCadenceAnchorEl(null);
      return;
    }

    await updateWorkspaceCadence({
      variables: {
        workspaceId,
        reviewCadenceDays: nextDays,
      },
    });

    setCadenceAnchorEl(null);
    await refetchEverything();
    showSuccessToast({ title: 'Review cadence updated successfully!' });
  };

  const saveNextReviewDate = async () => {
    await upsertObject({ nextDate: draftReviewDate });

    setLocalReviewDate(draftReviewDate);
    setIsEditingDate(false);

    if (draftReviewDate) {
      await syncUpcomingReviewEvent({
        dueDate: draftReviewDate,
        ownerValue: effectiveOwner,
      });
    }

    await refetchEverything();
    showSuccessToast({ title: 'Review date updated successfully!' });
  };

  const saveOwner = async () => {
    const nextOwner: OwnerLike | null = {
      id: draftOwner.id || null,
      text: draftOwner.text || null,
      email: draftOwner.email || null,
      memberId: draftOwner.memberId || null,
      inviteId: draftOwner.inviteId || null,
      profilePhotoPath: draftOwner.profilePhotoPath || null,
    };

    await upsertObject({ nextOwner });
    setLocalOwner(nextOwner);
    setIsEditingOwner(false);

    if (localReviewDate) {
      await syncUpcomingReviewEvent({
        dueDate: localReviewDate,
        ownerValue: nextOwner,
      });
    }

    await refetchEverything();
    showSuccessToast({ title: 'Review owner updated successfully!' });
  };

  const saveUpcomingNotes = async (value: string) => {
    if (!workspaceId || !localReviewDate) return;

    const payload = {
      ...(upcomingReviewEvent?.id ? { id: upcomingReviewEvent.id } : {}),
      eventType: EventKind.Review,
      objectType,
      objectId,
      dueDate: localReviewDate,
      completionStatus: (upcomingReviewEvent?.completionStatus || EventCompletionStatus.Open) as EventCompletionStatus,
      ...(buildOwnerInput(effectiveOwner) ? { assignedTo: buildOwnerInput(effectiveOwner) } : {}),
      notes: value || '',
    };

    await upsertEvent({
      variables: {
        workspaceId,
        eventData: [payload],
      },
    });

    await refetchEverything();
    showSuccessToast({ title: 'Review notes updated successfully!' });
  };

  if (!reviewsFeatureEnabled) return null;

  return (
    <ExpandSection title="REVIEWS" sectionId={`${objectType}_reviews`} onChange={setIsOpen}>
      <Div>
        <div className="panel">
          {error && (
            <Poppins color="error" px={14}>
              {error.message}
            </Poppins>
          )}

          {loading && !data ? (
            <Poppins px={14} color="cflowerBlue">
              Loading reviews...
            </Poppins>
          ) : (
            <div className="grid">
              <div>
                {!!avatarOwners.length && (
                  <AvatarPill>
                    <div className="avatars">
                      {avatarOwners.map((avatarOwner) => (
                        <StyledAvatar
                          key={ownerUniqueKey(avatarOwner)}
                          className="avatar"
                          $small
                          src={avatarOwner.profilePhotoPath || ''}
                        >
                          {getProfileCredentials(avatarOwner.text || avatarOwner.email || '')}
                        </StyledAvatar>
                      ))}
                    </div>
                  </AvatarPill>
                )}

                <ColumnTitle>Upcoming Reviews</ColumnTitle>

                <CadenceRow>
                  <Flex style={{ gap: 10 }}>
                    <AccessTimeOutlinedIcon
                      sx={{
                        color: (theme) => theme.palette.error.main,
                        fontSize: 18,
                      }}
                    />
                    <LabelText>Review Cadence</LabelText>
                  </Flex>
                  <ValueWithEdit>
                    <ValueText>{cadenceDisplayLabel}</ValueText>
                    {canEditCadence && (
                      <EditIconButton type="button" onClick={(e) => setCadenceAnchorEl(e.currentTarget)} disabled={saving}>
                        <PencilSvg />
                      </EditIconButton>
                    )}
                  </ValueWithEdit>
                </CadenceRow>
                <Menu anchorEl={cadenceAnchorEl} open={Boolean(cadenceAnchorEl)} onClose={() => setCadenceAnchorEl(null)}>
                  {cadenceOptions.map((option) => (
                    <MenuItem
                      key={option.label}
                      selected={option.label === cadenceLabel}
                      onClick={async () => {
                        await saveCadence(option.label);
                      }}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </Menu>

                <RowCard $danger={isUpcomingOverdue}>
                  <UpcomingMetaRow>
                    <Flex style={{ gap: 12, minWidth: 0 }}>
                      <IconFrame $warning={isUpcomingOverdue}>
                        {isUpcomingOverdue ? <WarningSvg /> : <Calendar />}
                      </IconFrame>
                      <div style={{ minWidth: 0 }}>
                        <LabelText>Next Review Date</LabelText>
                        <Flex style={{ gap: 10, flexWrap: 'wrap' }}>
                          <ValueText>{formatLongDate(localReviewDate, 'N/A')}</ValueText>
                          {canEdit && (
                            <EditIconButton
                              type="button"
                              onClick={() => {
                                setDraftReviewDate(localReviewDate || null);
                                setIsEditingDate(true);
                              }}
                            >
                              <PencilSvg />
                            </EditIconButton>
                          )}
                          {isUpcomingOverdue && <OverdueTag>Overdue</OverdueTag>}
                        </Flex>
                      </div>
                    </Flex>

                    <StatusPill
                      value={upcomingStatus}
                      disabled={!canEdit || saving || !localReviewDate}
                      onSelect={(status) =>
                        updateStatus({
                          status,
                          eventId: upcomingReviewEvent?.id,
                          dueDate: localReviewDate,
                          completedAt: upcomingReviewEvent?.completedAt,
                        })
                      }
                    />
                  </UpcomingMetaRow>

                  {isEditingDate && (
                    <InlineEditorRow>
                      <DateInputWrapper>
                        <MaterialDatePicker
                          value={draftReviewDate}
                          onChange={(date) => setDraftReviewDate(date)}
                          inputProps={{ placeholder: 'Select next review date' }}
                        />
                      </DateInputWrapper>
                      <InlineActions>
                        <TextButton
                          type="button"
                          onClick={() => {
                            setDraftReviewDate(localReviewDate || null);
                            setIsEditingDate(false);
                          }}
                          disabled={saving}
                        >
                          Cancel
                        </TextButton>
                        <TextButton type="button" $primary onClick={saveNextReviewDate} disabled={saving}>
                          Save
                        </TextButton>
                      </InlineActions>
                    </InlineEditorRow>
                  )}

                  <Divider />

                  <UpcomingMetaRow>
                    <Flex style={{ gap: 12 }}>
                      <OwnerIcon />
                      <LabelText>Owner</LabelText>
                    </Flex>

                    <ValueWithEdit>
                      <ValueText>{ownerName}</ValueText>
                      {canEdit && (
                          <EditIconButton
                            type="button"
                            onClick={() => {
                              setDraftOwner(asUserOption(effectiveOwner));
                              setIsEditingOwner(true);
                            }}
                          >
                          <PencilSvg />
                        </EditIconButton>
                      )}
                    </ValueWithEdit>
                  </UpcomingMetaRow>

                  {isEditingOwner && (
                    <InlineEditorRow>
                      <OwnerInputWrapper>
                        <UserInput
                          value={draftOwner}
                          onChange={setDraftOwner}
                          onSelectUser={setDraftOwner}
                          placeholder="Select owner"
                          disabled={saving}
                        />
                      </OwnerInputWrapper>
                      <InlineActions>
                        <TextButton
                          type="button"
                          onClick={() => {
                            setDraftOwner(asUserOption(effectiveOwner));
                            setIsEditingOwner(false);
                          }}
                          disabled={saving}
                        >
                          Cancel
                        </TextButton>
                        <TextButton type="button" $primary onClick={saveOwner} disabled={saving}>
                          Save
                        </TextButton>
                      </InlineActions>
                    </InlineEditorRow>
                  )}
                </RowCard>

                <ReviewNotes
                  notes={upcomingReviewEvent?.notes}
                  defaultOpen
                  editable
                  canEdit={canEdit}
                  saving={saving}
                  onSave={saveUpcomingNotes}
                  placeholderItems={['To Dos', 'Comments', 'Next steps']}
                />
              </div>

              <TimelineColumn>
                <ColumnTitle>Previous Reviews</ColumnTitle>

                {!previousReviews.length ? (
                  <EmptyState>No previous review events yet.</EmptyState>
                ) : (
                  <PreviousReviewsScroller className="styled-scroll styled-scroll--themed">
                    {previousReviews.map((event, idx) => {
                      const reviewDateValue = event.completedAt || event.dueDate || event.meta?.createdAt;
                      const notesOpenByDefault = !!event.notes && idx === 0;

                      return (
                        <TimelineItem key={event.id}>
                          <div className="marker">
                            <IconFrame $small>
                              <Calendar />
                            </IconFrame>
                          </div>

                          <TimelineHeader>
                            <div>
                              <LabelText>Review Date:</LabelText>
                              <ValueText>{formatLongDate(reviewDateValue, '-')}</ValueText>
                            </div>

                            <StatusPill
                              value={(event.completionStatus ?? EventCompletionStatus.Open) as StatusValue}
                              disabled={!canEdit || saving}
                              onSelect={(status) =>
                                updateStatus({
                                  status,
                                  eventId: event.id,
                                  dueDate: event.dueDate,
                                  completedAt: event.completedAt,
                                })
                              }
                            />
                          </TimelineHeader>

                          <Divider />

                          <ScheduledRow>
                            <div>
                              <LabelText>Review scheduled for:</LabelText>
                              <Poppins px={14} weight={500}>
                                {formatLongDate(event.dueDate, '-')}
                              </Poppins>
                            </div>

                            <RightAlign>
                              <LabelText>Completed by:</LabelText>
                              <Poppins px={14} weight={500}>
                                {event.completedBy?.text || event.completedBy?.email || 'N/A'}
                              </Poppins>
                            </RightAlign>
                          </ScheduledRow>

                          <ReviewNotes
                            notes={event.notes}
                            defaultOpen={notesOpenByDefault}
                            placeholderItems={['What changed', 'Next steps']}
                          />
                        </TimelineItem>
                      );
                    })}
                  </PreviousReviewsScroller>
                )}
              </TimelineColumn>
            </div>
          )}
        </div>
      </Div>
    </ExpandSection>
  );
};

export default ReviewsSection;
